import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button } from 'react-bootstrap';
import ImageDropperCropper from '../nonprofitProfile/ImageDropperCropper/ImageDropperCropper';
import API from '../../utils/API';
import { IOnImageChangeEvent, IOnImageDeleteEvent } from '../../types/shared.types';
import { getOriginalFileURLFromCroppedURL } from '../../modules/file/file.utils';
import './CustomizePageModal.css';
import MarkdownTextAreaInput from '../nonprofitProfile/EditModals/CampaignModal/MarkdownTextArea/MarkdownTextAreaInput';
import ReactTooltip from 'react-tooltip';

interface CustomizePageModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (data: CustomizePageData) => void;
  orgID?: string; // pass from parent for API calls
  thankYouOnly?: boolean; // if true, only show Thank You Message and Image fields
}

interface CustomizePageData {
  title: string;
  fundingGoal: string;
  description: string;
  logo: File | null;
  image: File | null;
  thankYouMessage: string;
  thankYouMessageAPI: string;
  thankYouImage: File | null;
  thankYouImageAPI: File | null;
  websiteLink: string;
}

const CustomizePageModal: React.FC<CustomizePageModalProps> = ({ show, onHide, onSave, orgID, thankYouOnly = false }) => {
  const reduxOrgId = useSelector((s: any) => s?.org?._id || s?.signin?.user?.orgID);
  const resolvedOrgID = orgID || reduxOrgId;
  const [formData, setFormData] = useState<CustomizePageData>({
    title: '',
    fundingGoal: '0',
    description: '',
    logo: null,
    image: null,
    thankYouMessage: '',
    thankYouMessageAPI: '',
    thankYouImage: null,
    thankYouImageAPI: null,
    websiteLink: ''
  });

  // const [showMarkdownPreview, setShowMarkdownPreview] = useState(false);
  const [primaryColor, setPrimaryColor] = useState<string>('#6FA03D');
  const [secondaryColor, setSecondaryColor] = useState<string>('#5271FF');
  const [uploadedUrls, setUploadedUrls] = useState<{ logoUrl?: string; bannerUrl?: string; thankYouUrl?: string; thankYouUrlAPI?: string }>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [embedDomains, setEmbedDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState<string>('');
  const [domainsError, setDomainsError] = useState<boolean>(false);
  const domainsRef = React.useRef<HTMLDivElement>(null);
  const [titleError, setTitleError] = useState<boolean>(false);
  const titleRef = React.useRef<HTMLInputElement>(null);
  const [brandingExists, setBrandingExists] = useState<boolean>(false);

  // auto-hide toast
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 2000);
    return () => clearTimeout(t);
  }, [message]);

  // Prefill on open
  useEffect(() => {
    (async () => {
      if (!show || !resolvedOrgID) return;
      setBrandingExists(false);
      try {
        const res = await fetch(`/api/branding/by-org/${resolvedOrgID}`);
        const json = await res.json();
        const b = json?.data;
        if (b) {
          setBrandingExists(true);
          setFormData(prev => ({
            ...prev,
            title: b.title || '',
            fundingGoal: String(b.goal || 0),
            description: b.about || '',
            thankYouMessage: b.thankYouMessage || '',
            thankYouMessageAPI: b.thankYouMessageAPI || '',
            websiteLink: b.links?.website || ''
          }));
          setPrimaryColor(b.colors?.primary || '#6FA03D');
          setSecondaryColor(b.colors?.secondary || '#5271FF');
          setUploadedUrls({
            logoUrl: b.images?.logoUrl || '',
            bannerUrl: b.images?.bannerUrl || '',
            thankYouUrl: b.images?.thankYouUrl || '',
            thankYouUrlAPI: b.images?.thankYouUrlAPI || ''
          });
          setEmbedDomains(Array.isArray(b.allowedEmbedDomains) ? b.allowedEmbedDomains : []);
        }
      } catch (e) {
        // ignore
      }
    })();
  }, [show, resolvedOrgID]);

  const handleInputChange = (field: keyof CustomizePageData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddDomain = () => {
    const trimmed = newDomain.trim().toLowerCase()
      .replace(/^https?:\/\//, '')
      .split('/')[0].split('?')[0];
    if (!trimmed || embedDomains.includes(trimmed)) {
      setNewDomain('');
      return;
    }
    setEmbedDomains(prev => [...prev, trimmed]);
    setNewDomain('');
    setDomainsError(false);
  };

  const handleRemoveDomain = (domain: string) => {
    setEmbedDomains(prev => prev.filter(d => d !== domain));
  };

  const handleImageChange = async (event: IOnImageChangeEvent) => {
    const { field, croppedImage, croppedImageFileName, originalImage, originalImageFileName } = event;
    const urlKey = field === 'logo' ? 'logoUrl' : field === 'image' ? 'bannerUrl' : field === 'thankYouImageAPI' ? 'thankYouUrlAPI' : 'thankYouUrl';
    const oldFileUrl = uploadedUrls[urlKey] || undefined;

    try {
      const res = await API.uploadFile(croppedImage, croppedImageFileName, oldFileUrl);
      // Stable S3 key (overwritten in place) => cache-bust so the browser refetches.
      const url = res?.data?.data?.url
        ? `${res.data.data.url}?v=${Date.now()}`
        : undefined;

      // Upload the full-res copy too, so "Edit Image" can re-crop later.
      if (originalImage && originalImageFileName) {
        await API.uploadFile(
          originalImage,
          originalImageFileName,
          oldFileUrl ? getOriginalFileURLFromCroppedURL(oldFileUrl) : undefined,
        ).catch((err: any) => console.error('original image upload failed (non-fatal)', err));
      }

      if (url) {
        setUploadedUrls(prev => ({ ...prev, [urlKey]: url }));
        setMessage({ type: 'success', text: 'Image uploaded successfully' });
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setMessage({ type: 'error', text: 'Image upload failed' });
    }
  };

  const handleImageDelete = async (event: IOnImageDeleteEvent) => {
    const { field } = event;
    const urlKey = field === 'logo' ? 'logoUrl' : field === 'image' ? 'bannerUrl' : field === 'thankYouImageAPI' ? 'thankYouUrlAPI' : 'thankYouUrl';
    setUploadedUrls(prev => ({ ...prev, [urlKey]: '' }));
  };

  const handleSave = async () => {
    try {
      if (!resolvedOrgID) {
        setMessage({ type: 'error', text: 'Organization not found' });
        return;
      }
      if (thankYouOnly && embedDomains.length === 0) {
        setDomainsError(true);
        domainsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (!thankYouOnly && !formData.title.trim()) {
        setTitleError(true);
        titleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        titleRef.current?.focus();
        return;
      }
      setTitleError(false);

      const brandingData = {
        orgID: resolvedOrgID,
        title: formData.title,
        about: formData.description,
        goal: parseFloat(formData.fundingGoal) || 0,
        images: {
          logoUrl: uploadedUrls.logoUrl || '',
          bannerUrl: uploadedUrls.bannerUrl || '',
          thankYouUrl: uploadedUrls.thankYouUrl || '',
          thankYouUrlAPI: uploadedUrls.thankYouUrlAPI || ''

        },
        thankYouMessage: formData.thankYouMessage,
        thankYouMessageAPI: formData.thankYouMessageAPI,
        links: {
          website: formData.websiteLink || ''
        },
        colors: {
          primary: primaryColor,
          secondary: secondaryColor,
          background: '#FFFFFF',
          text: '#000000'
        },
        seo: {
          title: formData.title,
          description: formData.description.substring(0, 160),
          imageUrl: ''
        },
        toggles: {
          showDonate: true,
          showImpact: true,
          showUpdates: true
        },
        allowedEmbedDomains: embedDomains
      };

      const response = await fetch(`/api/branding/${resolvedOrgID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(brandingData)
      });

      const result = await response.json();

      if (result.success) {
        onSave(result.data || formData);
        onHide();
        setMessage({ type: 'success', text: 'Profile saved' });
      } else {
        setMessage({ type: 'error', text: result.message || 'Error saving profile' });
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      setMessage({ type: 'error', text: 'Error saving profile. Please try again.' });
    }
  };

  const handleDelete = async () => {
    try {
      if (!resolvedOrgID) {
        setMessage({ type: 'error', text: 'Organization not found' });
        return;
      }
      if (!brandingExists) {
        setMessage({ type: 'error', text: 'Nothing to delete yet' });
        return;
      }

      // Only clear the fields that belong to the option currently open (thankYouOnly = Option A,
      // !thankYouOnly = Option B) — both options share the same branding document, so we must
      // not wipe the other option's fields. Use PUT (partial update) instead of DELETE.
      const brandingData = thankYouOnly
        ? {
          // Option A fields reset; Option B fields resent unchanged
          orgID: resolvedOrgID,
          title: formData.title,
          about: formData.description,
          goal: parseFloat(formData.fundingGoal) || 0,
          images: {
            logoUrl: uploadedUrls.logoUrl || '',
            bannerUrl: uploadedUrls.bannerUrl || '',
            thankYouUrl: uploadedUrls.thankYouUrl || '',
            thankYouUrlAPI: ''
          },
          thankYouMessage: formData.thankYouMessage,
          thankYouMessageAPI: '',
          links: {
            website: formData.websiteLink || ''
          },
          colors: {
            primary: primaryColor,
            secondary: secondaryColor,
            background: '#FFFFFF',
            text: '#000000'
          },
          seo: {
            title: formData.title,
            description: formData.description.substring(0, 160),
            imageUrl: ''
          },
          toggles: {
            showDonate: true,
            showImpact: true,
            showUpdates: true
          },
          allowedEmbedDomains: []
        }
        : {
          // Option B fields reset; Option A fields resent unchanged
          orgID: resolvedOrgID,
          title: "",
          about: "",
          goal: 0,
          images: {
            logoUrl: '',
            bannerUrl: '',
            thankYouUrl: '',
            thankYouUrlAPI: uploadedUrls.thankYouUrlAPI || ''
          },
          thankYouMessage: "",
          thankYouMessageAPI: formData.thankYouMessageAPI,
          links: {
            website: ''
          },
          colors: {
            primary: '#6FA03D',
            secondary: '#5271FF',
            background: '#FFFFFF',
            text: '#000000'
          },
          seo: {
            title: "",
            description: "",
            imageUrl: ''
          },
          toggles: {
            showDonate: true,
            showImpact: true,
            showUpdates: true
          },
          allowedEmbedDomains: embedDomains
        };

      const response = await fetch(`/api/branding/${resolvedOrgID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(brandingData)
      });

      const result = await response.json();

      if (result.success) {
        onSave(formData);

        if (thankYouOnly) {
          setFormData(prev => ({ ...prev, thankYouMessageAPI: '', thankYouImageAPI: null }));
          setUploadedUrls(prev => ({ ...prev, thankYouUrlAPI: '' }));
          setEmbedDomains([]);
          setNewDomain('');
        } else {
          setFormData(prev => ({
            ...prev,
            title: '',
            fundingGoal: '0',
            description: '',
            logo: null,
            image: null,
            thankYouMessage: '',
            thankYouImage: null,
            websiteLink: ''
          }));
          setPrimaryColor('#6FA03D');
          setSecondaryColor('#5271FF');
          setUploadedUrls(prev => ({ ...prev, logoUrl: '', bannerUrl: '', thankYouUrl: '' }));
        }
        onHide();
        setMessage({ type: 'success', text: 'Profile Deleted' });
      } else {
        setMessage({ type: 'error', text: result.message || 'Error saving profile' });
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      setMessage({ type: 'error', text: 'Error saving profile. Please try again.' });
    }
  };

  const handleCancel = () => {
    setFormData({
      title: '',
      fundingGoal: '0',
      description: '',
      logo: null,
      image: null,
      thankYouMessage: '',
      thankYouMessageAPI: '',
      thankYouImage: null,
      thankYouImageAPI: null,
      websiteLink: ''
    });
    setEmbedDomains([]);
    setNewDomain('');
    setDomainsError(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="customize-page-modal">
      <Modal.Header className="customize-modal-header">
        <div className="customize-modal-nav">
          <button className="customize-back-btn" onClick={onHide}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button className="customize-delete-btn" onClick={handleDelete}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Delete
          </button>
        </div>
      </Modal.Header>

      <Modal.Body className="customize-modal-body">
        <div className="customize-form">
          {!thankYouOnly && (
            <>
              {/* Organization Name Field */}
              <div className="customize-field">
                <label className="customize-label" style={titleError ? { color: '#ef4444' } : {}}>Organization Name</label>
                {/* <div className='col'> */}
                <input
                  ref={titleRef}
                  type="text"
                  className="customize-input"
                  style={titleError ? { borderColor: '#ef4444' } : {}}
                  value={formData.title}
                  onChange={(e) => {
                    if (titleError) setTitleError(false);
                    handleInputChange('title', e.target.value);
                  }}
                  maxLength={40}
                />
                <span className="customize-char-count ml-auto">{formData.title.length}/40 characters</span>
                {titleError && (
                  <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                    Organization Name is required
                  </div>
                )}
                {/* </div> */}

              </div>

              {/* Website Link Field */}
              <div className="customize-field mt-n3">
                <label className="customize-label">Website Link</label>
                <input
                  type="url"
                  className="customize-input"
                  value={formData.websiteLink}
                  onChange={(e) => handleInputChange('websiteLink', e.target.value)}
                  placeholder="https://example.com"
                />
              </div>

              {/* Funding Goal Field */}
              {/* <div className="customize-field">
                <label className="customize-label">Funding Goal</label>
                <input
                  type="number"
                  className="customize-input"
                  value={formData.fundingGoal}
                  onChange={(e) => handleInputChange('fundingGoal', e.target.value)}
                />
              </div> */}

              {/* Description Field */}
              <div className="customize-field">
                <label className="customize-label">
                  Description
                  <span
                    data-tip
                    data-for="whiteLabel-Description"
                    className="customize-info-icon">i
                    <ReactTooltip
                      id="whiteLabel-Description"
                      type="light"
                      effect="solid"
                    >
                      <div style={{ textAlign: 'left', fontSize: '1vw', maxWidth: 300 }}>
                        This description will be used in your donation page.
                        <br></br>
                        Make it good :)
                      </div>
                    </ReactTooltip>
                  </span>
                </label>
                <div style={{ border: '1px solid #D1D5DB' }}>
                  <MarkdownTextAreaInput
                    name="description"
                    value={formData.description}
                    setValue={(identifier, value: Record<string, string>) => {
                      handleInputChange('description', value['description'] || '');
                    }}
                    identifier={0}
                    maxCharacters={10000}
                  />
                </div>

                {/* <textarea
                  className="customize-textarea"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  maxLength={10000}
                  rows={4}
                />
                <div className="customize-textarea-footer">
                  <button 
                    className="customize-markdown-link"
                    onClick={() => setShowMarkdownPreview(!showMarkdownPreview)}
                  >
                    Show Markdown Preview
                  </button>
                  <span className="customize-char-count">{formData.description.length}/10000 characters</span>
                </div> */}
              </div>

              <div className="customize-field-wrap">
                {/* Logo Field */}
                <div className="customize-field form-group col flex-row">
                  <label className="customize-label form-label col-form-label col-sm-3">
                    Logo
                    <span
                      data-tip
                      data-for="whiteLabel-logo"
                      className="customize-info-icon">i
                      <ReactTooltip
                        id="whiteLabel-logo"
                        type="light"
                        effect="solid"
                      >
                        <div style={{ textAlign: 'left', fontSize: '1vw', maxWidth: 280 }}>
                          Recommended dimensions: <br /> at least 300x300 pixels
                        </div>
                      </ReactTooltip>
                    </span>
                  </label>
                  <ImageDropperCropper
                    key={`logo-${uploadedUrls.logoUrl || 'empty'}`}
                    defaultThumbNail={uploadedUrls.logoUrl || ''}
                    field="logo"
                    aspect="squareCrop"
                    fileName={`${resolvedOrgID}_logo_${Date.now()}`}
                    onImageChange={handleImageChange}
                    onImageDelete={handleImageDelete}
                  />
                </div>

                {/* Image Field */}
                <div className="customize-field form-group col flex-row">
                  <label className="customize-label form-label col-form-label col-sm-3">
                    Image
                    <span
                      data-tip
                      data-for="whiteLabel-image"
                      className="customize-info-icon">i
                      <ReactTooltip
                        id="whiteLabel-image"
                        type="light"
                        effect="solid"
                      >
                        <div style={{ textAlign: 'left', fontSize: '1vw', maxWidth: 280 }}>
                          Recommended dimensions: 1920x1080 pixels
                          <br></br>
                          max file size: 1mb
                        </div>
                      </ReactTooltip>
                    </span>
                  </label>
                  <ImageDropperCropper
                    key={`banner-${uploadedUrls.bannerUrl || 'empty'}`}
                    defaultThumbNail={uploadedUrls.bannerUrl || ''}
                    field="image"
                    aspect="defaultCrop"
                    fileName={`${resolvedOrgID}_banner_${Date.now()}`}
                    onImageChange={handleImageChange}
                    onImageDelete={handleImageDelete}
                  />
                </div>
              </div>

              <div className="customize-field-wrap">
                {/* Colors */}
                <div className="customize-field form-group col">
                  <label className="form-label col-form-label">Header Color</label>
                  <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="customize-input" style={{ height: 54, padding: 6 }} />
                </div>
                <div className="customize-field form-group col">
                  <label className="form-label col-form-label">Fonts Color</label>
                  <input type="color" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="customize-input" style={{ height: 54, padding: 6 }} />
                </div>
              </div>

              {/* Thank You Message Field */}
              <div className="customize-field">
                <label className="customize-label">Thank You Message</label>
                {/* <div className='col'> */}
                <textarea
                  className="customize-textarea"
                  value={formData.thankYouMessage}
                  onChange={(e) => handleInputChange('thankYouMessage', e.target.value)}
                  maxLength={500}
                  rows={3}
                />
                <span className="customize-char-count ml-auto">{formData.thankYouMessage.length}/500 characters</span>
                {/* </div> */}
              </div>

              {/* Thank You Image Field */}
              <div className="customize-field">
                <label className="customize-label form-label col-form-label">Thank You Image
                  <span
                    data-tip
                    data-for="whiteLabel-thankyou-image"
                    className="customize-info-icon">i
                    <ReactTooltip
                      id="whiteLabel-thankyou-image"
                      type="light"
                      effect="solid"
                    >
                      <div style={{ textAlign: 'left', fontSize: '1vw', maxWidth: 280 }}>
                        Recommended dimensions: 1920x1080 pixels
                        <br></br>
                        max file size: 1mb
                      </div>
                    </ReactTooltip>
                  </span>
                </label>

                <div className='col'>
                  <ImageDropperCropper
                    key={`thankyou-${uploadedUrls.thankYouUrl || 'empty'}`}
                    defaultThumbNail={uploadedUrls.thankYouUrl || ''}
                    field="thankYouImage"
                    aspect="defaultCrop"
                    fileName={`${resolvedOrgID}_thankyou_${Date.now()}`}
                    onImageChange={handleImageChange}
                    onImageDelete={handleImageDelete}
                  />
                </div>
              </div>
            </>
          )}

          {thankYouOnly && (
            <>
              {/* Allowed Embed Domains */}
              <div className="customize-field" ref={domainsRef} style={domainsError ? { border: '1.5px solid #ef4444', borderRadius: 8, padding: '12px', backgroundColor: '#fff5f5' } : {}}>
                <label className="customize-label" style={domainsError ? { color: '#ef4444' } : {}}>
                  Allowed Embed Domains <span style={{ color: '#ef4444' }}>*</span>
                  <span
                    data-tip
                    data-for="embed-domains-tip"
                    className="customize-info-icon">i
                    <ReactTooltip
                      id="embed-domains-tip"
                      type="light"
                      effect="solid"
                      place="right"
                    >
                      <div style={{ textAlign: 'left', fontSize: '1vw', maxWidth: 360 }}>
                        Add the domain(s) of the website(s) where your donation form is embedded.
                        This enables Apple Pay on those sites and restricts who can embed your form.
                        Enter the hostname only, e.g. <strong>giveto.org</strong>
                      </div>
                    </ReactTooltip>
                  </span>
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    className="customize-input"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDomain(); } }}
                    placeholder="e.g. giveto.org"
                    style={{ flex: 1, borderColor: domainsError ? '#ef4444' : undefined }}
                  />
                  <button
                    type="button"
                    onClick={handleAddDomain}
                    style={{
                      padding: '0 18px',
                      backgroundColor: '#5271FF',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Add
                  </button>
                </div>
                {embedDomains.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                    {embedDomains.map(domain => (
                      <span
                        key={domain}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '3px 12px',
                          backgroundColor: '#e8f8ff',
                          border: '1px solid #5271ff',
                          borderRadius: 25,
                          fontSize: 14,
                          fontWeight: 600,
                          color: '#5271ff',
                        }}
                      >
                        {domain}
                        <button
                          type="button"
                          onClick={() => handleRemoveDomain(domain)}
                          aria-label={`Remove ${domain}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'none',
                            border: 'none',
                            color: '#5271ff',
                            cursor: 'pointer',
                            padding: 0,
                            lineHeight: 1,
                            flexShrink: 0,
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="11" stroke="#5271ff" strokeWidth="2" />
                            <path d="M8 8l8 8M16 8l-8 8" stroke="#5271ff" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {domainsError && (
                  <p style={{ fontSize: 13, color: '#ef4444', fontWeight: 600, margin: '8px 0 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="11" stroke="#ef4444" strokeWidth="2" />
                      <path d="M12 7v5" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="12" cy="16.5" r="1" fill="#ef4444" />
                    </svg>
                    At least one allowed domain is required before saving.
                  </p>
                )}
                <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 0 0' }}>
                  Required for Apple Pay to work when your form is embedded on your website.
                </p>
              </div>

              {/* Thank You Message Field */}
              <div className="customize-field">
                <label className="customize-label">Thank You Message</label>
                {/* <div className='col'> */}
                <textarea
                  className="customize-textarea"
                  value={formData.thankYouMessageAPI}
                  onChange={(e) => handleInputChange('thankYouMessageAPI', e.target.value)}
                  maxLength={500}
                  rows={3}
                />
                <span className="customize-char-count ml-auto">{formData.thankYouMessageAPI.length}/500 characters</span>
                {/* </div> */}
              </div>

              {/* Thank You Image Field */}
              <div className="customize-field">
                <label className="customize-label form-label col-form-label">Thank You Image
                  <span
                    data-tip
                    data-for="whiteLabel-thankyou-image"
                    className="customize-info-icon">i
                    <ReactTooltip
                      id="whiteLabel-thankyou-image"
                      type="light"
                      effect="solid"
                    >
                      <div style={{ textAlign: 'left', fontSize: '1vw', maxWidth: 280 }}>
                        Recommended dimensions: 1920x1080 pixels
                        <br></br>
                        max file size: 1mb
                      </div>
                    </ReactTooltip>
                  </span>
                </label>

                <div className='col'>
                  <ImageDropperCropper
                    key={`thankyou-${uploadedUrls.thankYouUrlAPI || 'empty'}`}
                    defaultThumbNail={uploadedUrls.thankYouUrlAPI || ''}
                    field="thankYouImageAPI"
                    aspect="defaultCrop"
                    fileName={`${resolvedOrgID}_thankyou_${Date.now()}`}
                    onImageChange={handleImageChange}
                    onImageDelete={handleImageDelete}
                  />
                </div>
              </div>
            </>
          )}

          
        </div>
      </Modal.Body>

      <Modal.Footer className="customize-modal-footer">
        <Button variant="primary" className="customize-save-btn" onClick={handleSave}>
          Save
        </Button>
        <Button variant="secondary" className="customize-cancel-btn" onClick={handleCancel}>
          Cancel
        </Button>
      </Modal.Footer>

      {/* Toast */}
      {message && (
        <div style={{
          position: 'fixed',
          top: 20,
          right: 20,
          padding: '12px 16px',
          borderRadius: 8,
          color: '#fff',
          fontWeight: 600,
          fontSize: 14,
          backgroundColor: message.type === 'success' ? '#10b981' : '#ef4444',
          boxShadow: '0 4px 12px rgba(0,0,0,.15)',
          zIndex: 2000
        }}>
          {message.text}
        </div>
      )}
    </Modal>
  );
};

export default CustomizePageModal;
